import { useCallback, useState, useEffect, useRef } from "react";
import PageTitle from "../utils/PageTitle";
import { useUserContext } from "../../contexts/UserContext";
import { catchError } from "../../services/DaoService";
import { Button, Card, Col, Input, Row, Select, Spin, Empty } from "antd";
import { PlusCircleFilled, ReloadOutlined } from "@ant-design/icons";
import { AffaireService } from "../../services/AffaireService";
import AffaireCreateForm from "./AffaireCreateForm";
import AffaireBacklogItem from "./AffaireBacklogItem";
import { ModeleService } from "../../services/ModeleService";
import { PrioriteService } from "../../services/PrioriteService";
import { EtapeService } from "../../services/EtapeService";
import PropTypes from "prop-types";

export default function AffaireListByDepartement({ departement }) {
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newModalVisible, setNewModalVisible] = useState(false);
  const { check } = useUserContext();
  const [searchText, setSearchText] = useState("");
  const [statut, setStatut] = useState("tout");
  const [affaires, setAffaires] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [etapes, setEtapes] = useState([]);
  const [priorites, setPriorites] = useState([]);
  const [modeleId, setModeleId] = useState(null);
  const [etapeId, setEtapeId] = useState(null);
  const [prioriteId, setPrioriteId] = useState(null);
  const sentinelRef = useRef(null);

  const searchParams = {
    searchText: searchText || null,
    statut,
    modele_id: modeleId,
    etape_id: etapeId,
    priorite_id: prioriteId,
    showMines: true,
    departement_id: departement.id,
  };

  const init = useCallback(() => {
    setLoading(true);
    AffaireService.getInstance()
      .search(searchParams)
      .then((response) => {
        setPaginationData(response.data);
        setAffaires(response.data.data);
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, statut, modeleId, etapeId, prioriteId, departement]);

  useEffect(() => {
    Promise.all([
      ModeleService.getInstance().all(),
      EtapeService.getInstance().all(),
      PrioriteService.getInstance().all(),
    ])
      .then(([m, e, p]) => { setModeles(m.data); setEtapes(e.data); setPriorites(p.data); })
      .catch(catchError);
  }, []);

  useEffect(() => { init(); }, [init]);

  const loadMore = useCallback(() => {
    if (!paginationData?.next_page_url || loadingMore) return;
    setLoadingMore(true);
    AffaireService.post(paginationData.next_page_url, searchParams)
      .then((response) => {
        setPaginationData(response.data);
        setAffaires(prev => [...prev, ...response.data.data]);
      })
      .catch(catchError)
      .finally(() => setLoadingMore(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleUpdate = (updated) => {
    setAffaires(prev => prev.map(a => (a.uid === updated.uid ? updated : a)));
  };

  return (
    <>
      <PageTitle
        title="Dossiers du département"
        subTitle={paginationData?.total ? `${affaires.length} sur ${paginationData.total}` : ''}
      >
        {check("CREATE-AFFAIRE") && (
          <Button type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>
            Nouveau dossier
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={init}>Rafraîchir</Button>
      </PageTitle>

      <Card styles={{ body: { padding: 0 } }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-default, #E8EAED)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onSearch={init}
            placeholder="Rechercher par nom ou code"
            allowClear
            style={{ maxWidth: 280 }}
          />
          <Select value={statut} onChange={setStatut} style={{ width: 140 }}>
            <Select.Option value="tout">Tous</Select.Option>
            <Select.Option value="pending">En cours</Select.Option>
            <Select.Option value="done">Clôturés</Select.Option>
          </Select>
          <Select showSearch optionFilterProp="label" placeholder="Type" allowClear style={{ width: 160 }}
            options={modeles.map(m => ({ value: m.id, label: m.nom }))}
            onChange={setModeleId} />
          <Select showSearch optionFilterProp="label" placeholder="Étape" allowClear style={{ width: 160 }}
            options={etapes.map(e => ({ value: e.id, label: e.nom }))}
            onChange={setEtapeId} />
          <Select showSearch optionFilterProp="label" placeholder="Priorité" allowClear style={{ width: 160 }}
            options={priorites.map(p => ({ value: p.id, label: p.nom }))}
            onChange={setPrioriteId} />
        </div>
        <Spin spinning={loading && affaires.length === 0}>
          {affaires.length === 0 && !loading
            ? <div style={{ padding: '24px 0' }}><Empty description="Aucun dossier" /></div>
            : (
              <div>
                {affaires.map(affaire => (
                  <div key={affaire.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                    <AffaireBacklogItem affaire={affaire} onUpdate={handleUpdate} />
                  </div>
                ))}
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore && <div style={{ textAlign: 'center', padding: '10px 0' }}><Spin size="small" /></div>}
              </div>
            )
          }
        </Spin>
      </Card>

      {newModalVisible && (
        <AffaireCreateForm
          departement={departement}
          visible={newModalVisible}
          onCreate={() => { init(); setNewModalVisible(false); }}
          onCancel={() => setNewModalVisible(false)}
        />
      )}
    </>
  );
}

AffaireListByDepartement.propTypes = {
  departement: PropTypes.object.isRequired,
};
